import { useEffect } from "react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { Tabs, TabsProps } from "antd5";
import { frameworkIdLayoutRoute } from "router/routes/frameworks";

import { DetailsPage } from "components/app_layout/DetailsLayout";
import FrameworkHeader from "components/frameworks/FrameworkHeader";
import Tooltip from "components/ui/tooltip/Tooltip";
import { useDescribeFramework } from "lib/hooks/api/frameworks/useDescribeFramework";
import { useFrameworkHighlighting } from "lib/hooks/api/frameworks/useFrameworkHighlighting";
import { useLogEntityViewOnOpen } from "lib/hooks/api/userActivity/useLogEntityView";
import { useSubscription } from "lib/hooks/subscription";
import { useConfirmUserDataSubscription } from "lib/providers/Paywall";
import * as tracking from "lib/tracking";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { LoggedEntityType } from "lib/types/graphQLEnums";
import { removeTrailingSlash } from "lib/utils";

import css from "./FrameworkDetailsPage.module.scss";

enum PrimaryTab {
  OVERVIEW = "/",
  BUYERS = "/buyers",
  SUPPLIERS = "/suppliers",
  CALL_OFFS = "/call-offs",
}

const primaryTabToLabel: Record<PrimaryTab, string> = {
  [PrimaryTab.OVERVIEW]: "Overview",
  [PrimaryTab.BUYERS]: "Buyers",
  [PrimaryTab.SUPPLIERS]: "Suppliers",
  [PrimaryTab.CALL_OFFS]: "Call-offs",
};

const TAB_PATHS: PrimaryTab[] = [PrimaryTab.BUYERS, PrimaryTab.SUPPLIERS, PrimaryTab.CALL_OFFS];

/** Picks the active tab from the current pathname. Falls back to Overview. */
function getActiveTab(pathname: string, frameworkPrefix: string): PrimaryTab {
  if (!pathname.startsWith(frameworkPrefix)) return PrimaryTab.OVERVIEW;
  const tabSegment = removeTrailingSlash(pathname.slice(frameworkPrefix.length));
  return TAB_PATHS.find((tab) => tab === tabSegment) ?? PrimaryTab.OVERVIEW;
}

export function FrameworkDetailsPage() {
  const { framework_id } = frameworkIdLayoutRoute.useParams();

  const subscription = useSubscription();
  const { logEvent } = useTracking();

  // Redirects a user without the FRAMEWORKS data type if they somehow got here (e.g. by direct link).
  useConfirmUserDataSubscription("FRAMEWORKS", "FRAMEWORKS");
  const hasFrameworksEnabled = subscription.hasDataTypes("FRAMEWORKS");
  useLogEntityViewOnOpen(
    LoggedEntityType.Framework,
    hasFrameworksEnabled ? framework_id : undefined,
  );

  const { data: framework } = useDescribeFramework(framework_id, {
    onSuccess: (data) => {
      document.title = `${data.framework.title}`;
    },
    enabled: hasFrameworksEnabled,
  });

  const { highlightTerms } = useFrameworkHighlighting(framework_id);

  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const frameworkPrefix = `/frameworks/${framework_id}`;
  const selectedTab = getActiveTab(pathname, frameworkPrefix);

  // Manual page-view tracking — `frameworkIdLayoutRoute` has `pageName: ""`
  // so the router-level auto-tracker skips it; we fire one enriched event
  // per framework with the framework ID attached for analytics.
  useEffect(() => {
    if (framework_id) {
      tracking.pageView("Framework details", { "Framework ID": framework_id });
    }
  }, [framework_id]);

  const tabs: TabsProps["items"] = [
    {
      key: PrimaryTab.OVERVIEW,
      label: "Overview",
    },
    {
      key: PrimaryTab.SUPPLIERS,
      label: (
        <Tooltip
          content={
            framework?.suppliers?.length !== 0 ? undefined : (
              <span>
                No suppliers have been awarded to this framework.
                <br />
                <br />
                This can often be the case for frameworks that are not yet live
              </span>
            )
          }
        >
          <span>Suppliers</span>
        </Tooltip>
      ),
      // Stops this component and by extension the APIs it calls from being rendered until the framework has been fetched
      disabled: !framework || framework?.suppliers?.length === 0,
    },
    {
      key: PrimaryTab.BUYERS,
      label: (
        <Tooltip
          content={
            framework?.buyers?.length ? undefined : (
              <span>
                No specific buyers can be identified for this framework.
                <br />
                <br />
                This can sometimes be the case for frameworks that are not yet live
              </span>
            )
          }
        >
          <span>Buyers</span>
        </Tooltip>
      ),
      disabled: !framework?.buyers?.length,
    },
    { key: PrimaryTab.CALL_OFFS, label: "Call-off notices", disabled: false },
  ];

  return (
    <TrackingProvider data={{ "Framework ID": framework_id }}>
      <DetailsPage>
        <div className={css.pageHeader}>
          <div className={css.headerComponents}>
            <FrameworkHeader framework={framework} titleHighlight={highlightTerms?.title || []} />
            <Tabs
              className={css.menuTabs}
              defaultActiveKey={PrimaryTab.OVERVIEW}
              aria-label="Framework details tabs"
              activeKey={selectedTab}
              onChange={(key) => {
                const tabSegment = key === PrimaryTab.OVERVIEW ? "" : key;
                void navigate({
                  to: `/frameworks/${framework_id}${tabSegment}`,
                  search: (prev: Record<string, unknown>) => prev,
                });
                logEvent(EventNames.tabChanged, {
                  "Tab selected": primaryTabToLabel[key as PrimaryTab],
                });
              }}
              items={tabs}
              tabBarStyle={{ marginBottom: "0px" }}
            />
          </div>
        </div>
        {/* Only mount the active tab once the FRAMEWORKS subscription is
            confirmed. Without this gate, child route components would fire
            their own framework / highlight queries during the brief window
            before useConfirmUserDataSubscription redirects unsubscribed
            users to the paywall. */}
        <div className={css.pageWrapper}>{hasFrameworksEnabled ? <Outlet /> : null}</div>
      </DetailsPage>
    </TrackingProvider>
  );
}
